import React, { useState } from "react";
import { ChevronDown, ChevronUp, AlertCircle, Download } from "lucide-react";
import Button from "./Button";

export const TableComponent = ({
  columns = [],
  data = [],
  onRowClick = null,
  selectable = false,
  selectedRows = [],
  onSelectRows = null,
  sortable = true,
  pagination = true,
  pageSize = 10,
  loading = false,
  emptyMessage = "No data available",
  tableTitle = "Table Title",
  download = null,
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [currentPage, setCurrentPage] = useState(1);
  const [selectAll, setSelectAll] = useState(false);

  // Sorting logic
  const sortedData = React.useMemo(() => {
    if (!sortConfig.key) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig]);

  // Pagination logic
  const paginatedData = React.useMemo(() => {
    if (!pagination) return sortedData;
    const start = (currentPage - 1) * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, currentPage, pageSize, pagination]);

  const totalPages = Math.ceil(sortedData.length / pageSize);

  const handleSort = (key) => {
    if (!sortable) return;
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const handleSelectAll = () => {
    if (selectAll) {
      onSelectRows([]);
    } else {
      onSelectRows(paginatedData.map((_, idx) => idx));
    }
    setSelectAll(!selectAll);
  };

  const handleSelectRow = (index) => {
    if (selectedRows.includes(index)) {
      onSelectRows(selectedRows.filter((i) => i !== index));
    } else {
      onSelectRows([...selectedRows, index]);
    }
  };

  if (loading) {
    return (
      <div className="w-full bg-white rounded-lg border border-[#e3eae2] p-8">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="w-8 h-8 border-3 border-emerald-700 border-t-transparent rounded-full animate-spin" />
          <p className="text-[#64748B]">Loading data...</p>
        </div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="w-full bg-white rounded-lg border border-[#e3eae2] p-8">
        <div className="flex flex-col items-center justify-center gap-2">
          <AlertCircle size={40} className="text-[#64748B]" />
          <p className="text-[#64748B]">{emptyMessage}</p>
        </div>
      </div>
    );
  }


  return (
    <>
      <div
        class="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0   p-2.5 rounded-lg bg-white border border-[#cfd7ce]"
        style={{
          boxShadow:
            "0px 4px 8px -2px rgba(16,24,40,0.1), 0px 2px 4px -2px rgba(16,24,40,0.06);",
        }}
      >
        <div class="flex flex-col justify-start items-start self-stretch flex-grow-0 flex-shrink-0 bg-white">
          <div class="flex justify-start items-center self-stretch flex-grow-0 flex-shrink-0 gap-4 px-6 pt-5 pb-[19px]">
            {/* table title */}
            <div class="flex justify-start items-center flex-grow relative gap-2">
              <p class="flex-grow-0 flex-shrink-0 text-lg font-medium text-left text-[#131811]">
                {tableTitle}
              </p>
            </div>
            {/* download button */}
            {download && (
              <Button
                onClick={download}
                variant="outline"
                icon={<Download strokeWidth={2.25} size={30} />}
              >
                Download {tableTitle} Report (CSV)
              </Button>
            )}
          </div>
        </div>

        <div className="w-full bg-white rounded-lg border border-[#e3eae2] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              {/* Header */}
              <thead className="bg-[#e9f1e8] border-b border-[#e3eae2]">
                <tr>
                  {selectable && (
                    <th className="w-12 px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        className="w-4 h-4 accent-emerald-700 cursor-pointer"
                      />
                    </th>
                  )}
                  {columns.map((column) => (
                    <th
                      key={column.key}
                      onClick={() =>
                        column.sortable !== false && handleSort(column.key)
                      }
                      className={`px-4 py-3 text-left text-sm font-semibold text-[#191d23] ${
                        sortable && column.sortable !== false
                          ? "cursor-pointer hover:bg-gray-100 select-none"
                          : ""
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortable && column.sortable !== false && (
                          <div className="flex flex-col">
                            <ChevronUp
                              size={14}
                              className={`-mb-1 ${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "asc"
                                  ? "text-emerald-700"
                                  : "text-gray-300"
                              }`}
                            />
                            <ChevronDown
                              size={14}
                              className={`-mt-1 ${
                                sortConfig.key === column.key &&
                                sortConfig.direction === "desc"
                                  ? "text-emerald-700"
                                  : "text-gray-300"
                              }`}
                            />
                          </div>
                        )}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>

              {/* Body */}
              <tbody>
                {paginatedData.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    onClick={() => onRowClick && onRowClick(row, rowIndex)}
                    className={`border-b border-gray-200 transition-colors ${
                      onRowClick ? "cursor-pointer hover:bg-emerald-50" : ""
                    } ${
                      selectedRows.includes(rowIndex)
                        ? "bg-emerald-50"
                        : "bg-white"
                    }`}
                  >
                    {selectable && (
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedRows.includes(rowIndex)}
                          onChange={() => handleSelectRow(rowIndex)}
                          onClick={(e) => e.stopPropagation()}
                          className="w-4 h-4 accent-emerald-700 cursor-pointer"
                        />
                      </td>
                    )}
                    {columns.map((column) => (
                      <td
                        key={column.key}
                        className="px-4 py-3 text-sm text-[#191d23]"
                      >
                        {column.render
                          ? column.render(row[column.key], row, rowIndex)
                          : row[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination && totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#e3eae2] bg-gray-50">
              <p className="text-sm text-[#64748B]">
                Showing {(currentPage - 1) * pageSize + 1} to{" "}
                {Math.min(currentPage * pageSize, sortedData.length)} of{" "}
                {sortedData.length} results
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(1, prev - 1))
                  }
                  disabled={currentPage === 1}
                  className="px-3 py-1 rounded border border-[#d8dae5] text-sm text-[#191d23] 
                hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Previous
                </button>

                {[...Array(totalPages)].map((_, idx) => {
                  const page = idx + 1;
                  // Show first, last, current, and adjacent pages
                  if (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`w-8 h-8 rounded text-sm transition-colors ${
                          currentPage === page
                            ? "bg-emerald-700 text-white"
                            : "border border-[#d8dae5] text-[#191d23] hover:bg-gray-100"
                        }`}
                      >
                        {page}
                      </button>
                    );
                  } else if (
                    page === currentPage - 2 ||
                    page === currentPage + 2
                  ) {
                    return (
                      <span key={page} className="text-[#64748B]">
                        ...
                      </span>
                    );
                  }
                  return null;
                })}

                <button
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                  }
                  disabled={currentPage === totalPages}
                  className="px-3 py-1 rounded border border-[#d8dae5] text-sm text-[#191d23] 
                hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
